/**
 * One bot: gross P&L and position, every resting rung against the market,
 * its limits, and the lots / orders / fills / activity behind it.
 *
 * Fees, CGT and net are computed by the API but deliberately not shown here
 * for now — users know their costs; the screen leads with gross.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { Button, Card } from '../../components/UI';
import { useBreakpoint } from '../../lib/useBreakpoint';
import { useLiveFeed } from '../../lib/liveFeed';
import { MarketClock } from '../../components/MarketClock';
import { BookQueue, MarketInfo, Tape } from './BookQueue';
import { getBot, getConnection, isWorking, type BotDetail, type BotOrderView, type BotSummary, type ConnectionStatus } from '../../lib/bots';
import { RowActions } from './Bots';
import {
  BandGauge,
  BotsStyles,
  Caption,
  CardHead,
  Empty,
  EnvPill,
  ErrorLine,
  FlashNumber,
  HealthPill,
  Kpi,
  LiveBadge,
  Meter,
  Notice,
  Segmented,
  StatePill,
  TableWrap,
  Td,
  Th,
  fmt,
  fmtInt,
  mono,
  pageStyle,
  pktDateTime,
  pktTime,
  pnlColor,
  signed,
  usePoll,
} from './ui';

interface Props {
  id: string;
  onBack: () => void;
  onOpenSymbol: (sym: string) => void;
}

type Tab = 'open' | 'closed' | 'orders' | 'fills' | 'activity';

/** Room the page footer needs below us: its padding plus one line of text. */
const FOOTER_ALLOWANCE = 92;
/** Below this the panels are too squeezed to read, so we let the page scroll. */
const MIN_FIT_HEIGHT = 560;

export const BotDetails = ({ id, onBack, onOpenSymbol }: Props) => {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isDesktop = bp === 'desktop';
  const [bot, setBot] = useState<BotDetail | null>(null);
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<Tab>('open');
  /**
   * The grid's own workings — lots, rungs, round trips — are behind this.
   * At a glance the screen shows what the broker would tell you; the lot view
   * explains HOW the grid got there, which is interesting but is not the basis
   * of anything, least of all CGT.
   */
  const [showDetails, setShowDetails] = useState(false);
  /** The book says what is offered; the tape says what actually traded. */
  const [marketView, setMarketView] = useState<'book' | 'tape'>('book');
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  // Depth for this bot's symbol. Subscribing is what makes the worker open the
  // MBP connection; it costs the broker session nothing.
  const live = useLiveFeed(bot?.params.symbol ?? null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState<number | null>(null);

  const load = async () => {
    try {
      const [detail, conn] = await Promise.all([getBot(id), getConnection().catch(() => null)]);
      setBot(detail.bot);
      if (conn) setStatus(conn.status);
      setError(null);
      setLoadedAt(Date.now());
    } catch (e) {
      const err = e as { status?: number; message: string };
      if (err.status === 404) setMissing(true);
      setError(err.message);
    }
  };
  usePoll(load, 3000);

  /**
   * How much height this page actually has. The top bar above and the footer
   * below are both outside this component, so `100dvh` would overflow by the
   * height of each. Measuring our own offset adapts to whatever chrome is
   * there, including a top bar that wraps on a narrower window.
   */
  useLayoutEffect(() => {
    if (!isDesktop) {
      setAvail(null);
      return;
    }
    const measure = () => {
      const top = (pageRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY;
      setAvail(Math.max(MIN_FIT_HEIGHT, window.innerHeight - top - FOOTER_ALLOWANCE));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isDesktop]);

  const act = async (_bot: BotSummary, fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      const err = e as { status?: number; message: string };
      setError(err.message);
      if (err.status === 404) onBack();
    } finally {
      setBusy(false);
    }
  };

  if (missing || !bot) {
    return (
      <div style={pageStyle(isMobile)}>
        <BotsStyles />
        <BackButton onBack={onBack} />
        {missing ? <Notice tone="down">This bot no longer exists.</Notice> : error ? <ErrorLine>{error}</ErrorLine> : <Empty>Loading…</Empty>}
      </div>
    );
  }

  const ready = status?.state === 'ready' && !status.unavailable && status.env === bot.env;
  const closed = bot.closedLots;
  const wins = closed.filter((c) => c.gross > 0).length;
  const mark = bot.position.mark;
  const price = mark.last ?? mark.price;
  // Fit the whole bot on one screen only when a desktop actually gives us the
  // height for it. On a short window we fall back to scrolling rather than
  // squeezing every panel past readability.
  const fit = isDesktop && avail !== null && avail > MIN_FIT_HEIGHT;
  const fromFirst = price && bot.firstReference ? ((price - bot.firstReference) / bot.firstReference) * 100 : null;

  const copyId = () => {
    void navigator.clipboard?.writeText(bot.id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    });
  };

  return (
    // On a desktop the whole bot fits one screen: the page itself never
    // scrolls, and the panels that can outgrow their box scroll inside it.
    // Narrower than that there is no room for the trick, so it stacks and the
    // page scrolls as before.
    <div
      ref={pageRef}
      style={{
        ...pageStyle(isMobile),
        gap: 14,
        ...(fit ? { height: avail!, overflow: 'hidden', paddingTop: 16, paddingBottom: 0 } : null),
      }}
    >
      <BotsStyles />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <BackButton onBack={onBack} />
        <button
          type="button"
          onClick={() => onOpenSymbol(bot.symbol)}
          title="Open this symbol in Markets"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--tk-ink)', fontSize: 28, fontWeight: 700, letterSpacing: '-0.03em', fontFamily: 'var(--tk-sans)' }}
        >
          {bot.symbol}
        </button>
        <button
          type="button"
          className="tkb-ghost"
          onClick={copyId}
          title={`Copy bot id ${bot.id}`}
          style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11.5, ...mono }}
        >
          {copied ? 'copied' : bot.label}
        </button>
        <EnvPill env={bot.env} />
        <StatePill state={bot.state} />
        <HealthPill health={bot.health} />
        <div style={{ flex: 1 }} />
        {/* Orders are day orders: they expire at the close and re-post at the
            next open, so how long is left is part of reading this screen. */}
        <MarketClock />
        <LiveBadge at={loadedAt} stale={!!error} />
        <span style={{ display: 'inline-flex', gap: 8 }}>
          <RowActions bot={bot} busy={busy} canResume={ready} act={act} />
        </span>
      </div>

      {/* Things the user must not miss. These are warnings, not help text. */}
      {bot.archivedAt && <Notice tone="warn">Archived. These records remain part of account P&amp;L.</Notice>}
      {bot.state === 'STARTING' && bot.waitingFor && <Notice tone="warn">Waiting for {bot.waitingFor}.</Notice>}
      {/* A restart recovers by itself now: it adopts the orders the broker
          confirms and posts whichever leg is missing. Only an order the broker
          never acknowledged still needs a person, and then the reason says
          which one — so do not tell everyone else to press Stop. */}
      {bot.state === 'INTERRUPTED' &&
        (bot.error ? (
          <Notice tone="down">
            The engine restarted and this bot could <b>not</b> be brought back on its own: {bot.error}
          </Notice>
        ) : (
          <Notice tone="warn">
            The engine restarted while this bot had orders working. It is <b>not trading yet</b> and comes back by itself once the broker’s order list has confirmed what is really working
            {bot.waitingFor ? <> — waiting for {bot.waitingFor}</> : null}. Nothing to do.
          </Notice>
        ))}
      {bot.state === 'ERROR' && <Notice tone="down">{bot.error}</Notice>}
      {/* The one failure a deposit fixes. Say so, and say what is still at the
          exchange: the bot stopped posting but did not cancel anything. */}
      {bot.state === 'CASH_STARVED' && (
        <Notice tone="down">
          <b>Cash starved.</b> The broker refused an order because the account cannot cover it: {bot.error}. Nothing more is posted
          {bot.resting.length > 0 ? <>; <b>{bot.resting.length} order(s) are still resting</b> and may fill or expire at the close</> : null}. Add cash, then
          {bot.resting.length > 0 ? ' Stop (to cancel what is resting) and Resume.' : ' Resume.'}
        </Notice>
      )}
      {bot.resting.some((o) => o.uncertain) && <Notice tone="down">An order was sent but never acknowledged. It is not resent — check the broker’s order list.</Notice>}
      <ErrorLine>{error}</ErrorLine>

      {/* Everything below the header shares one row on a desktop: performance
          and the ladder, the book, then limits and history. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isDesktop ? 'minmax(0, 1.45fr) minmax(0, 0.95fr) minmax(0, 1.2fr)' : isTablet ? 'repeat(2, minmax(0, 1fr))' : '1fr',
          gap: 14,
          alignItems: 'stretch',
          ...(fit ? { flex: 1, minHeight: 0 } : null),
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
        <Card padding={20} style={{ display: 'flex', flexDirection: 'column' }}>
          <CardHead
            title="Performance"
            right={<span title="Delivery batches consumed FIFO, with acquisition charges included">on the broker’s basis</span>}
          />
          {bot.account.issues.length > 0 && <Notice tone="warn">Accounting estimate: {bot.account.issues.join('; ')}.</Notice>}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'minmax(0, 1.2fr) repeat(3, minmax(0, 1fr))', gap: 14, alignItems: 'start' }}>
            {[
              {
                label: 'Realised',
                hint: 'Net proceeds less the FIFO delivery cost, plus same-day trading results. CGT is estimated separately.',
                value: bot.account.realised,
                big: true,
              },
              {
                label: 'Unrealised',
                hint: 'What the position is worth now, less what it cost to open — charges included, so it shows a gain only once the position has covered its own entry cost.',
                value: bot.account.unrealised ?? 0,
                big: false,
              },
              {
                label: 'Total',
                hint: 'Realised plus unrealised. Unaffected by how lots are matched — only the split between the two moves.',
                value: bot.account.total ?? bot.account.realised,
                big: false,
              },
              {
                label: 'Charges',
                hint: 'Brokerage, its sales tax and the levies — already inside the figures above, shown so the cost is visible.',
                value: -(bot.account.chargesInCost + bot.account.chargesRealised),
                big: false,
              },
            ].map((k) => (
              <div key={k.label} title={k.hint} style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                <Caption>{k.label}</Caption>
                {/* Same row height for all three so the figures share a baseline. */}
                <div style={{ height: isMobile ? 34 : 36, display: 'flex', alignItems: 'flex-end' }}>
                  <FlashNumber
                    value={k.value}
                    text={signed(k.value)}
                    style={{ fontSize: k.big ? (isMobile ? 30 : 32) : 20, fontWeight: k.big ? 700 : 600, letterSpacing: k.big ? '-0.035em' : '-0.02em', lineHeight: 1, color: pnlColor(k.value) }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 16,
              paddingTop: 14,
              borderTop: '1px solid var(--tk-line-2)',
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
              gap: 14,
            }}
          >
            <Kpi label="Shares held" hint={`Bought ${fmtInt(bot.account.boughtQuantity)} · sold ${fmtInt(bot.account.soldQuantity)}`} value={fmtInt(bot.account.quantity)} />
            <Kpi label="Avg cost" hint="Average cost of surviving delivery batches, including charges" value={fmt(bot.account.avgCost, 4)} />
            <Kpi label="Exposure" hint="What the held shares cost, charges included" value={fmt(bot.account.costBasis)} />
            <Kpi label="Market value" hint={mark.price ? `At the ${mark.basis} ${fmt(mark.price)}` : 'No price yet'} value={<FlashNumber value={bot.position.marketValue} text={fmt(bot.position.marketValue)} />} />
          </div>
        </Card>

        <Card padding={20} style={{ display: 'flex', flexDirection: 'column', flex: fit ? 1 : undefined, minHeight: 0 }}>
          <CardHead title="Order ladder" right={mark.at ? `quote ${pktTime(mark.at)}` : 'no quote'} />
          <OrderLadder bot={bot} price={price ?? null} />
        </Card>
        </div>

        {/* Where the resting pair actually sits in the book. Market feed only —
            the broker is never asked anything, the bots hold that session. */}
        <Card padding={20} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <CardHead
            title={
              <span style={{ display: 'inline-flex', gap: 3, padding: 3, background: 'var(--tk-surface-2)', border: '1px solid var(--tk-line)', borderRadius: 'var(--tk-radius-sm)' }}>
                {(['book', 'tape'] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => setMarketView(v)}
                    title={v === 'book' ? 'Resting depth, and what is queued ahead of our orders' : 'The prints themselves — what actually traded'}
                    style={{ padding: '4px 10px', fontSize: 11.5, fontWeight: 600, letterSpacing: '0.02em', fontFamily: 'var(--tk-mono)', border: 'none', borderRadius: 6, cursor: 'pointer', background: marketView === v ? 'var(--tk-surface)' : 'transparent', color: marketView === v ? 'var(--tk-ink)' : 'var(--tk-ink-3)', boxShadow: marketView === v ? 'var(--tk-shadow-sm)' : 'none' }}
                  >
                    {v === 'book' ? 'Book & queue' : 'Tape'}
                  </button>
                ))}
              </span>
            }
          />
          {/* Both are as long as the exchange makes them; they scroll, the page does not. */}
          <div style={{ flex: fit ? 1 : undefined, minHeight: 0, overflowY: 'auto', marginRight: -6, paddingRight: 6 }}>
            {marketView === 'book' ? <BookQueue bot={bot} live={live} /> : <Tape live={live} sym={bot.symbol} />}
          </div>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0, gridColumn: isTablet ? '1 / -1' : undefined }}>
      {/* The market the bot is quoting into — above its own limits, because
          when a bot stops making sense this is what explains it. */}
      <MarketInfo live={live} sym={bot.symbol} />

      {/* Limits */}
      <Card padding={20}>
        <CardHead
          title="Limits"
          right={`${fmtInt(bot.params.lotSize)} sh · ±${fmt(bot.params.spreadPct)}% · ${fmtInt(bot.buysFilled)} buys / ${fmtInt(bot.sellsFilled)} sells`}
        />
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: isMobile ? 20 : 22 }}>
          <Meter label="Orders today" used={bot.ordersToday} limit={bot.params.maxOrders || null} hint={`${fmtInt(bot.ordersPlaced)} orders in total`} />
          <Meter label="Inventory" used={bot.inventory} limit={bot.params.maxInventory || null} unit="sh" hint="Buying pauses at the cap" />
          <BandGauge label="Price stop" pct={fromFirst} band={bot.params.stopPct} hint={bot.firstReference ? `Measured from the first reference ${fmt(bot.firstReference)}` : undefined} />
        </div>
      </Card>

      {/* History */}
      <Card padding={0} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: fit && showDetails ? 1 : undefined, minHeight: 0 }}>
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          title="The grid's own lots and round trips — how the position was built, rung by rung"
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '13px 18px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--tk-ink)', fontSize: 13.5, fontWeight: 600, textAlign: 'left' }}
        >
          <span style={{ display: 'inline-block', transition: 'transform 0.18s var(--tk-ease)', transform: showDetails ? 'rotate(90deg)' : 'none', color: 'var(--tk-ink-3)' }}>▸</span>
          {showDetails ? 'Hide details' : 'Show details'}
          <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--tk-ink-3)', ...mono }}>
            {showDetails ? 'the grid, lot by lot' : `${fmtInt(bot.openLots.length)} open lots · ${fmtInt(closed.length)} round trips${closed.length ? ` · ${Math.round((wins / closed.length) * 100)}% won` : ''}`}
          </span>
        </button>
        {showDetails && (
        <div style={{ padding: '0 18px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <Segmented
            value={tab}
            onChange={setTab}
            options={[
              { id: 'open', label: 'Open lots', count: bot.openLots.length },
              { id: 'closed', label: 'Closed lots', count: closed.length },
              { id: 'orders', label: 'Orders', count: bot.orders.length },
              { id: 'fills', label: 'Fills', count: bot.fills.length },
              { id: 'activity', label: 'Activity' },
            ]}
          />
          <span style={{ fontSize: 11.5, color: 'var(--tk-ink-3)', ...mono }}>created {pktDateTime(bot.createdAt)}</span>
        </div>
        )}
        {showDetails && (
        /* The tables are as long as the bot's history; they scroll here. */
        <div style={{ padding: '0 12px 12px', flex: fit ? 1 : undefined, minHeight: 0, overflowY: 'auto' }}>
          {tab === 'open' && <OpenLots bot={bot} />}
          {tab === 'closed' && <ClosedLots bot={bot} />}
          {tab === 'orders' && <Orders orders={bot.orders} />}
          {tab === 'fills' && <Fills bot={bot} />}
          {tab === 'activity' && <Activity bot={bot} />}
        </div>
        )}
      </Card>
        </div>
      </div>
    </div>
  );
};

const BackButton = ({ onBack }: { onBack: () => void }) => (
  <Button size="sm" variant="ghost" onClick={onBack}>
    ← Bots
  </Button>
);

/**
 * The resting pair against the market. The marker slides between the two
 * rails; the chips say how far each order is from filling.
 */
/**
 * Every resting order, as rungs, with the market's true place among them.
 *
 * This used to show one order a side, picked with `.find()` — which returned
 * whatever came first in the array rather than the rung nearest the market. A
 * ladder bot holds two to four a side (depth 2, and the per-side ceiling lets
 * a forced mirror push past it), so the old widget hid most of the book and
 * frequently pointed at the wrong end of it.
 *
 * Rungs run high to low down the page, which is the way a book is read, and
 * the market line is drawn where the price actually falls between them rather
 * than at a fixed midpoint.
 */
const OrderLadder = ({ bot, price }: { bot: BotDetail; price: number | null }) => {
  const acquire = bot.resting.find((o) => o.role === 'ACQUIRE');
  const rungs = bot.resting.filter((o) => o.role !== 'ACQUIRE');
  const mark = bot.position.mark;

  if (rungs.length === 0 && !acquire) {
    return <Empty>{isWorking(bot.state) ? 'Nothing resting right now.' : 'The bot is not working.'}</Empty>;
  }
  if (acquire) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Caption>Opening purchase</Caption>
        <span style={{ fontSize: 20, fontWeight: 600, ...mono }}>
          BUY {fmtInt(acquire.quantity)} @ {fmt(acquire.price)}
        </span>
        <Meter label={acquire.status.toLowerCase()} used={acquire.executed} limit={acquire.quantity} unit="sh" />
      </div>
    );
  }

  const byPrice = (a: BotOrderView, b: BotOrderView) => b.price - a.price;
  const sells = rungs.filter((o) => o.side === 'SELL').sort(byPrice);
  const buys = rungs.filter((o) => o.side === 'BUY').sort(byPrice);
  // The rungs the market reaches first: the cheapest sell and the dearest buy.
  const innerSell = sells[sells.length - 1];
  const innerBuy = buys[0];
  const spread = innerSell && innerBuy ? innerSell.price - innerBuy.price : null;

  /** One rung. The inner one on each side is the next to fill, so it leads. */
  const Rung = ({ order, inner }: { order: BotOrderView; inner: boolean }) => {
    const isBuy = order.side === 'BUY';
    const color = isBuy ? 'var(--tk-up)' : 'var(--tk-down)';
    const soft = isBuy ? 'var(--tk-up-soft)' : 'var(--tk-down-soft)';
    const away = price ? ((order.price - price) / price) * 100 : null;
    const pending = order.status === 'PENDING' || order.cancelRequested || order.uncertain;
    return (
      <div
        title={`${order.status.toLowerCase()}${order.cancelRequested ? ' · cancelling' : ''}${order.uncertain ? ' · unconfirmed' : ''}${order.brokerOrderId ? ` · broker ${order.brokerOrderId}` : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: inner ? '8px 12px' : '5px 12px',
          borderRadius: 'var(--tk-radius-sm)',
          background: inner ? soft : 'var(--tk-surface-2)',
          opacity: pending ? 0.55 : 1,
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color, width: 30, flexShrink: 0 }}>
          {order.side}
        </span>
        <span
          style={{
            fontSize: inner ? 19 : 14,
            fontWeight: inner ? 700 : 600,
            letterSpacing: '-0.02em',
            lineHeight: 1,
            color: inner ? 'var(--tk-ink)' : 'var(--tk-ink-2)',
            ...mono,
          }}
        >
          {fmt(order.price)}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', ...mono }}>
            {fmtInt(order.quantity)} sh{order.executed ? ` · ${fmtInt(order.executed)} done` : ''}
          </span>
          {away !== null && (
            <span style={{ fontSize: 11, fontWeight: 600, color, width: 56, textAlign: 'right', ...mono }}>
              {signed(away)}%
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Caption>
          {rungs.length} resting · {buys.length} buy / {sells.length} sell
        </Caption>
        {spread !== null && (
          <span style={{ fontSize: 10.5, color: 'var(--tk-ink-3)', ...mono }} title="Between the two innermost rungs">
            grid {fmt(spread)}
          </span>
        )}
      </div>

      {sells.map((o) => (
        <Rung key={o.id} order={o} inner={o === innerSell} />
      ))}

      {/* The market, drawn between the sides rather than at a fixed midpoint. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0' }}>
        <div style={{ flex: 1, height: 1, background: 'var(--tk-accent)', opacity: 0.4 }} />
        {price ? (
          <>
            <span style={{ width: 9, height: 9, borderRadius: 999, background: 'var(--tk-accent)', flexShrink: 0 }} />
            <FlashNumber value={price} text={fmt(price)} style={{ fontSize: 14, fontWeight: 700 }} />
          </>
        ) : (
          <span style={{ fontSize: 11.5, color: 'var(--tk-ink-3)' }}>no quote</span>
        )}
        <div style={{ flex: 1, height: 1, background: 'var(--tk-accent)', opacity: 0.4 }} />
      </div>

      {buys.map((o) => (
        <Rung key={o.id} order={o} inner={o === innerBuy} />
      ))}

      {sells.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--tk-ink-3)', padding: '4px 12px' }}>nothing to sell</div>
      )}
      {buys.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--tk-ink-3)', padding: '4px 12px' }}>no buy resting</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 11, color: 'var(--tk-ink-3)', paddingTop: 6, marginTop: 'auto', ...mono }}>
        <span>{mark.bid ? `bid ${fmt(mark.bid)}` : ''}</span>
        <span>{bot.reference ? `ref ${fmt(bot.reference)}` : ''}</span>
        <span>{mark.ask ? `ask ${fmt(mark.ask)}` : ''}</span>
      </div>
    </div>
  );
};

const OpenLots = ({ bot }: { bot: BotDetail }) =>
  bot.openLots.length === 0 ? (
    <Empty>No open lots.</Empty>
  ) : (
    <TableWrap maxHeight={460}>
      <thead>
        <tr>
          <Th left>Lot</Th>
          <Th left>Opened</Th>
          <Th>Shares</Th>
          <Th>Buy price</Th>
          <Th>Cost</Th>
          <Th>Sells at</Th>
          <Th>Unrealised</Th>
          <Th>%</Th>
        </tr>
      </thead>
      <tbody>
        {bot.openLots.map((l, i) => (
          <tr key={l.lotNo} className="tkb-row">
            <Td left bold title={l.source === 'holding' ? 'Held before the bot started' : 'Bought by the bot'}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                #{l.lotNo}
                {i === 0 ? <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 999, background: 'var(--tk-accent-soft)', color: 'var(--tk-accent)' }}>next</span> : null}
              </span>
            </Td>
            <Td left color="var(--tk-ink-3)">
              {pktDateTime(l.openedAt)}
            </Td>
            <Td>{fmtInt(l.quantity)}</Td>
            <Td>{fmt(l.buyPrice)}</Td>
            <Td>{fmt(l.cost)}</Td>
            <Td>{fmt(l.gridSell)}</Td>
            <Td color={pnlColor(l.unrealised)} bold>
              {signed(l.unrealised)}
            </Td>
            <Td color={pnlColor(l.unrealisedPct)}>{signed(l.unrealisedPct)}%</Td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );

const ClosedLots = ({ bot }: { bot: BotDetail }) =>
  bot.closedLots.length === 0 ? (
    <Empty>No closed lots yet.</Empty>
  ) : (
    <TableWrap maxHeight={460}>
      <thead>
        <tr>
          <Th left>Closed</Th>
          <Th left>Cycle</Th>
          <Th>Shares</Th>
          <Th>Buy</Th>
          <Th>Sell</Th>
          <Th>Gross P&amp;L</Th>
        </tr>
      </thead>
      <tbody>
        {bot.closedLots.map((c) => (
          <tr key={c.lotNo} className="tkb-row">
            <Td left color="var(--tk-ink-3)">
              {pktDateTime(c.closedAt)}
            </Td>
            <Td left color={c.sameDay ? 'var(--tk-up)' : 'var(--tk-ink-3)'} title={c.sameDay ? 'Brokerage charged once; nothing settled' : 'Carried overnight: brokerage both sides plus CDC'}>
              {c.sameDay ? 'same-day' : 'carried'}
            </Td>
            <Td>{fmtInt(c.quantity)}</Td>
            {/* A lot's cost can be a weighted average across an opening
                purchase's partial fills, so it need not land on a whole paisa.
                Two decimals here would make the row look like it does not foot. */}
            <Td title={c.buyPrice !== Number(c.buyPrice.toFixed(2)) ? `Exactly ${c.buyPrice.toFixed(6)} — a weighted average across partial fills` : undefined}>
              {fmt(c.buyPrice, c.buyPrice !== Number(c.buyPrice.toFixed(2)) ? 4 : 2)}
            </Td>
            <Td>{fmt(c.sellPrice)}</Td>
            <Td color={pnlColor(c.gross)} bold>
              {signed(c.gross)}
            </Td>
          </tr>
        ))}
        <tr>
          <Td left bold>
            Total
          </Td>
          <Td />
          <Td bold>{fmtInt(bot.closedLots.reduce((s, c) => s + c.quantity, 0))}</Td>
          <Td />
          <Td />
          <Td bold color={pnlColor(bot.pnl.gross)}>
            {signed(bot.pnl.gross)}
          </Td>
        </tr>
      </tbody>
    </TableWrap>
  );

const statusColor = (s: string) => (s === 'FILLED' ? 'var(--tk-up)' : s === 'REJECTED' || s === 'ERROR' ? 'var(--tk-down)' : s === 'CANCELLED' ? 'var(--tk-ink-3)' : 'var(--tk-warn)');

const Orders = ({ orders }: { orders: BotOrderView[] }) =>
  orders.length === 0 ? (
    <Empty>No orders yet.</Empty>
  ) : (
    <TableWrap maxHeight={460}>
      <thead>
        <tr>
          <Th left>Time</Th>
          <Th left>Side</Th>
          <Th>Shares</Th>
          <Th>Limit</Th>
          <Th left>Status</Th>
          <Th>Filled</Th>
          <Th>Avg fill</Th>
          <Th left>Broker id</Th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.id} className="tkb-row" title={[o.exchangeOrderId ? `exchange ${o.exchangeOrderId}` : '', o.message].filter(Boolean).join(' · ') || undefined}>
            <Td left color="var(--tk-ink-3)">
              {pktDateTime(o.createdAt)}
            </Td>
            <Td left bold color={o.side === 'BUY' ? 'var(--tk-up)' : 'var(--tk-down)'}>
              {o.role}
            </Td>
            <Td>{fmtInt(o.quantity)}</Td>
            <Td>{fmt(o.price)}</Td>
            <Td left color={statusColor(o.status)}>
              {o.status.toLowerCase()}
              {o.uncertain ? ' (unconfirmed)' : ''}
            </Td>
            <Td>{fmtInt(o.executed)}</Td>
            <Td>{o.executed ? fmt(o.avgFillPrice) : '—'}</Td>
            <Td left color="var(--tk-ink-3)">
              {o.brokerOrderId || '—'}
            </Td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );

const Fills = ({ bot }: { bot: BotDetail }) =>
  bot.fills.length === 0 ? (
    <Empty>No fills yet.</Empty>
  ) : (
    <TableWrap maxHeight={460}>
      <thead>
        <tr>
          <Th left>Time</Th>
          <Th left>Side</Th>
          <Th>Shares</Th>
          <Th>Price</Th>
          <Th>Value</Th>
          <Th left>Exchange id</Th>
        </tr>
      </thead>
      <tbody>
        {bot.fills.map((f, i) => (
          <tr key={`${f.orderId}-${i}`} className="tkb-row" title={[f.brokerOrderId ? `broker ${f.brokerOrderId}` : '', `seen via ${f.source}`].filter(Boolean).join(' · ')}>
            <Td left color="var(--tk-ink-3)">
              {pktDateTime(f.at)}
            </Td>
            <Td left bold color={f.side === 'BUY' ? 'var(--tk-up)' : 'var(--tk-down)'}>
              {f.side}
            </Td>
            <Td>{fmtInt(f.quantity)}</Td>
            <Td>{fmt(f.price)}</Td>
            <Td>{fmt(f.price * f.quantity)}</Td>
            <Td left color="var(--tk-ink-3)">
              {f.exchangeOrderId || '—'}
            </Td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );

const EVENT_COLOR: Record<string, string> = { error: 'var(--tk-down)', warn: 'var(--tk-warn)', fill: 'var(--tk-up)' };

const Activity = ({ bot }: { bot: BotDetail }) =>
  bot.events.length === 0 ? (
    <Empty>Nothing yet.</Empty>
  ) : (
    <div style={{ maxHeight: 460, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      {bot.events.map((e, i) => (
        <div key={i} className="tkb-row" style={{ display: 'grid', gridTemplateColumns: '10px 128px 1fr', alignItems: 'start', gap: 12, padding: '9px 10px', borderBottom: '1px solid var(--tk-line-2)', fontSize: 12.5 }}>
          <span style={{ width: 7, height: 7, marginTop: 6, borderRadius: 999, background: EVENT_COLOR[e.kind] ?? 'var(--tk-line)' }} />
          <span style={{ color: 'var(--tk-ink-3)', ...mono }}>{pktDateTime(e.at)}</span>
          <span style={{ color: EVENT_COLOR[e.kind] ?? 'var(--tk-ink)', lineHeight: 1.45 }}>{e.message}</span>
        </div>
      ))}
    </div>
  );
